import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import CrisisBanner from '../components/CrisisBanner';
import ScreenshotUpload from '../components/ScreenshotUpload';
import AIResponseCard from '../components/AIResponseCard';
import ConversationActionsCard from '../components/ConversationActionsCard';
import ShareCard from '../components/ShareCard';
import ClosingRitual from '../components/ClosingRitual';
import { CheckCircle2, Camera, Star } from 'lucide-react';
import { toast } from 'sonner';

const FeedbackModal = ({ onSubmit, onSkip }) => {
  const [feedback, setFeedback] = useState('');
  const [rating, setRating] = useState(0);

  return (
    <Card className="card-surface border-[#D4AF37] mb-6" data-testid="feedback-modal">
      <CardHeader>
        <CardTitle className="text-center" style={{ fontFamily: 'Outfit' }}>
          We're glad we could help
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-[#A1A1AA] text-center text-sm">Your feedback helps us be better for the next person who needs support.</p>
        <div className="flex justify-center gap-2" data-testid="rating-stars">
          {[1, 2, 3, 4, 5].map((star) => (
            <button
              key={star}
              type="button"
              onClick={() => setRating(star)}
              className="transition-transform hover:scale-110"
              data-testid={`rating-star-${star}`}
            >
              <Star className={`w-8 h-8 ${star <= rating ? 'fill-[#D4AF37] text-[#D4AF37]' : 'text-[#3F3F46]'}`} />
            </button>
          ))}
        </div>
        <Textarea
          value={feedback}
          onChange={(e) => setFeedback(e.target.value)}
          placeholder="What helped you the most? Anything we can do better? (optional)"
          rows={3}
          className="bg-[#0A0A0A] border-[#27272A] focus:ring-[#D4AF37] resize-none"
          data-testid="feedback-text"
        />
        <div className="flex gap-3">
          <Button
            onClick={() => onSubmit(feedback, rating)}
            className="btn-gold flex-1 rounded-full"
            data-testid="submit-feedback-button"
          >
            Submit Feedback
          </Button>
          <Button
            onClick={onSkip}
            variant="ghost"
            className="text-[#71717A] hover:text-[#A1A1AA]"
            data-testid="skip-feedback-button"
          >
            Skip
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

const ResultPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const result = location.state?.result;
  const showCrisisFromHome = location.state?.showCrisisBanner || false;
  const [showCrisisBanner, setShowCrisisBanner] = useState(showCrisisFromHome || result?.crisis_detected || false);
  const [showScreenshotUpload, setShowScreenshotUpload] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [showRitual, setShowRitual] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [thread, setThread] = useState([]);

  useEffect(() => {
    if (!result?.conversation_id) return;
    let cancelled = false;
    const loadThread = async () => {
      try {
        const res = await api.get(`/conversation/${result.conversation_id}/messages`);
        if (cancelled) return;
        const msgs = Array.isArray(res.data) ? res.data : [];
        // Ensure the just-sent turn appears even if backend hasn't indexed it yet
        const hasLatest = msgs.some((m) => m.user_message === result.user_message && m.ai_response === result.ai_response);
        const combined = hasLatest ? msgs : [...msgs, {
          message_id: 'latest',
          conversation_id: result.conversation_id,
          user_message: result.user_message,
          ai_response: result.ai_response,
          detected_tones: result.detected_tones || [],
          response_style: result.response_style,
          created_at: new Date().toISOString()
        }];
        setThread(combined);
      } catch {
        // Fallback: show only the latest turn if history fetch fails
        setThread([{
          message_id: 'latest',
          conversation_id: result.conversation_id,
          user_message: result.user_message,
          ai_response: result.ai_response,
          detected_tones: result.detected_tones || [],
          response_style: result.response_style,
          created_at: new Date().toISOString()
        }]);
      }
    };
    loadThread();
    // Keep scroll at the top so user can re-read from the start
    if (typeof window !== 'undefined') window.scrollTo(0, 0);
    return () => { cancelled = true; };
  }, [result?.conversation_id, result?.user_message, result?.ai_response, result?.detected_tones, result?.response_style]);

  if (!result) {
    navigate('/');
    return null;
  }

  const closeConversation = async (feedback = '', rating = 0) => {
    try {
      await api.post(`/conversation/${result.conversation_id}/close`, {
        feedback: feedback || null,
        rating: rating || null
      });
      localStorage.removeItem('activeConversation');
      toast.success('Thank you. Take care of yourself.');
      setShowFeedback(false);
      setShowRitual(true);
    } catch {
      toast.error('Failed to close conversation');
      setIsClosing(false);
      setShowFeedback(false);
    }
  };

  const handleShareDone = () => {
    setShowShare(false);
    navigate('/');
  };

  const handleRitualDone = () => {
    setShowRitual(false);
    setShowShare(true);
  };

  const handleImHelped = () => {
    setIsClosing(true);
    setShowFeedback(true);
  };

  const handleFeedbackSubmit = async (feedback, rating) => {
    await closeConversation(feedback, rating);
  };

  const handleFeedbackSkip = async () => {
    await closeConversation();
  };

  const handleContinueTalking = () => {
    localStorage.setItem('activeConversation', JSON.stringify({
      conversation_id: result.conversation_id,
      title: result.user_message.substring(0, 50)
    }));
    toast.success('Continue sharing your thoughts in the same conversation');
    navigate('/');
  };

  const handleScreenshotUpload = async (screenshotData) => {
    try {
      await api.post(`/conversation/${screenshotData.conversationId}/screenshot`, {
        conversation_id: screenshotData.conversationId,
        image_data: screenshotData.image,
        verified_color: screenshotData.verifiedColor
      });
      setShowScreenshotUpload(false);
    } catch {
      toast.error('Failed to upload screenshot. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] py-12 px-4" data-testid="result-page">
      <CrisisBanner show={showCrisisBanner} onDismiss={() => setShowCrisisBanner(false)} severity={result?.crisis_level || 'moderate'} />
      <div className="max-w-4xl mx-auto">
        {thread.length === 0 ? (
          <>
            <Card className="card-surface border-[#27272A] border-l-4 border-l-[#8B5CF6] mb-4" data-testid="your-message-card">
              <CardHeader><CardTitle className="text-sm text-[#A1A1AA]" style={{ fontFamily: 'Outfit' }}>You</CardTitle></CardHeader>
              <CardContent><p className="text-[#F9FAFB] text-base leading-relaxed" data-testid="user-message-text">{result.user_message}</p></CardContent>
            </Card>
            <AIResponseCard result={result} />
          </>
        ) : (
          thread.map((msg, idx) => (
            <div key={msg.message_id || `turn-${idx}`} data-testid={`conversation-turn-${idx}`}>
              <Card className="card-surface border-[#27272A] border-l-4 border-l-[#8B5CF6] mb-4" data-testid={`your-message-card-${idx}`}>
                <CardHeader><CardTitle className="text-sm text-[#A1A1AA]" style={{ fontFamily: 'Outfit' }}>You</CardTitle></CardHeader>
                <CardContent><p className="text-[#F9FAFB] text-base leading-relaxed" data-testid={`user-message-text-${idx}`}>{msg.user_message}</p></CardContent>
              </Card>
              <AIResponseCard result={msg} />
            </div>
          ))
        )}
        {showShare ? (
          <ShareCard onDone={handleShareDone} />
        ) : showRitual ? (
          <ClosingRitual onDone={handleRitualDone} />
        ) : showFeedback ? (
          <FeedbackModal onSubmit={handleFeedbackSubmit} onSkip={handleFeedbackSkip} />
        ) : (
          <ConversationActionsCard onHelped={handleImHelped} onContinue={handleContinueTalking} isClosing={isClosing} />
        )}
      </div>
    </div>
  );
};

export default ResultPage;
